import DateTimePicker, { type DateTimePickerEvent } from '@react-native-community/datetimepicker';
import { Ionicons } from '@expo/vector-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

type Props = {
  label?: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  error?: string | null;
  minimumDate?: Date;
};

function formatDisplay(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function DatePickerField({
  label,
  value,
  onChange,
  placeholder,
  error,
  minimumDate,
}: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const effectivePlaceholder = placeholder ?? t('common.datePickerPlaceholder');
  const [open, setOpen] = useState(false);
  // Interim value used inside the iOS modal before confirming.
  const [interim, setInterim] = useState<Date>(value ?? new Date());

  function handleAndroidChange(_: DateTimePickerEvent, selected?: Date) {
    setOpen(false);
    if (selected) onChange(selected);
  }

  function openPicker() {
    setInterim(value ?? new Date());
    setOpen(true);
  }

  if (Platform.OS === 'android') {
    return (
      <View style={styles.wrap}>
        {!!label && <Text style={styles.label}>{label}</Text>}
        <Pressable
          style={[styles.field, !!error && styles.fieldError]}
          onPress={openPicker}
          accessibilityRole="button"
        >
          <Text style={[styles.fieldText, !value && styles.placeholder]}>
            {value ? formatDisplay(value) : effectivePlaceholder}
          </Text>
          <Ionicons name="calendar-outline" size={18} color={colors.textMuted} />
        </Pressable>
        {!!error && <Text style={styles.errorText}>{error}</Text>}
        {open && (
          <DateTimePicker
            value={interim}
            mode="date"
            display="default"
            minimumDate={minimumDate}
            onChange={handleAndroidChange}
          />
        )}
      </View>
    );
  }

  // iOS — spinner inside Modal
  return (
    <View style={styles.wrap}>
      <Text style={styles.label}>{label}</Text>
      <Pressable
        style={[styles.field, !!error && styles.fieldError]}
        onPress={openPicker}
        accessibilityRole="button"
      >
        <Text style={[styles.fieldText, !value && styles.placeholder]}>
          {value ? formatDisplay(value) : effectivePlaceholder}
        </Text>
        <Ionicons name="calendar-outline" size={18} color={colors.textMuted} />
      </Pressable>
      {!!error && <Text style={styles.errorText}>{error}</Text>}

      <Modal visible={open} transparent animationType="slide" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.overlay} onPress={() => setOpen(false)}>
          <Pressable style={styles.sheet} onPress={(e) => e.stopPropagation()}>
            <View style={styles.sheetHeader}>
              <Pressable
                onPress={() => setOpen(false)}
                hitSlop={12}
                accessibilityRole="button"
              >
                <Text style={styles.btnCancel}>{t('common.cancel')}</Text>
              </Pressable>
              <Text style={styles.sheetTitle}>{label}</Text>
              <Pressable
                onPress={() => { onChange(interim); setOpen(false); }}
                hitSlop={12}
                accessibilityRole="button"
              >
                <Text style={styles.btnDone}>{t('common.datePickerDone')}</Text>
              </Pressable>
            </View>
            <DateTimePicker
              value={interim}
              mode="date"
              display="spinner"
              minimumDate={minimumDate}
              locale="el-GR"
              onChange={(_: DateTimePickerEvent, selected?: Date) => { if (selected) setInterim(selected); }}
              style={styles.spinner}
            />
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    wrap: {
      gap: spacing.xs,
    },
    label: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '500',
    },
    field: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: 52,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingHorizontal: spacing.md,
    },
    fieldError: {
      borderColor: colors.danger,
    },
    fieldText: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    placeholder: {
      color: colors.textMuted,
      fontWeight: '400',
    },
    errorText: {
      color: colors.danger,
      fontSize: fontSize.sm,
    },
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.4)',
      justifyContent: 'flex-end',
    },
    sheet: {
      backgroundColor: colors.surface,
      borderTopLeftRadius: radius.xl,
      borderTopRightRadius: radius.xl,
      paddingBottom: 32,
    },
    sheetHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    sheetTitle: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    btnCancel: {
      color: colors.textMuted,
      fontSize: fontSize.md,
    },
    btnDone: {
      color: colors.primary,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    spinner: {
      height: 200,
    },
  });
}
